import { useQuery } from '@tanstack/react-query';
import { useEmbeddedApiClient } from '@/hooks/useEmbeddedApiClient';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';

/**
 * Embedded-safe demo-data status.
 *
 * The standalone `useDemoData` reaches the API through `useAuthFetch`, which
 * depends on Clerk (`useStableGetToken`) and the standalone Role context — neither
 * is mounted under `/embedded/*`, so calling it there crashes the dashboard. This
 * hook uses the cookie-based embedded fetch instead and drives the embedded
 * onboarding narrative.
 */
export function useEmbeddedDemoData(): {
  hasDemoData: boolean;
  recordCount: number;
  loading: boolean;
} {
  const { apiClient } = useEmbeddedApiClient();
  const { orgId } = useEmbeddedOrg();

  const { data, isLoading } = useQuery({
    // Scope the cache by org so a re-auth into a different portal/org can't serve
    // the previous org's demo-status from cache.
    queryKey: ['embedded', 'demo-data', 'status', orgId],
    // The embedded client rewrites /api/v1/* -> /api/v1/embedded/*, attaches the
    // org/session params the embedded middleware needs, validates the body against
    // the api-types schema and logs a mismatch.
    queryFn: () => apiClient.demo.getStatus(),
    // Without the org the embedded fetch rejects ("Organization not loaded"): do not
    // send the first request until the session has produced one.
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });

  return {
    hasDemoData: data?.hasDemoData ?? false,
    recordCount: data?.recordCount ?? 0,
    loading: isLoading,
  };
}
